import { useCallback } from "react";
import { Messages } from "../../../core/api";

interface UsePOCDocumentQuestionProps {
  questionId: number;
  currentAnswer: Messages["POCItemsDocumentAnswer"][] | null;
  readonly: boolean;
  onClickWhileDisabled: () => void;
  onInsertAnswer: (id: number, answer: Messages["POCItemsDocumentAnswer"][]) => void;
}

export function usePOCDocumentQuestion({
  questionId,
  currentAnswer,
  readonly,
  onClickWhileDisabled,
  onInsertAnswer,
}: UsePOCDocumentQuestionProps) {
  const handleTaskCheck = useCallback(
    (itemName: string) => {
      if (readonly) {
        onClickWhileDisabled();
        return;
      }

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex(
        (answer) => answer.item.toLowerCase() === itemName.toLowerCase()
      );

      if (existingAnswerIndex >= 0) {
        // Remove the answer if it exists
        const newAnswers = currentAnswers.filter((_, index) => index !== existingAnswerIndex);
        onInsertAnswer(questionId, newAnswers);
      } else {
        // Add new answer
        const newAnswer: Messages["POCItemsDocumentAnswer"] = {
          item: itemName,
          attributes: {
            type: "EveryVisit",
          },
        };
        onInsertAnswer(questionId, [...currentAnswers, newAnswer]);
      }
    },
    [currentAnswer, onInsertAnswer, onClickWhileDisabled, questionId, readonly]
  );

  const handleWhenToPerformChange = useCallback(
    (itemName: string, whenToPerform: Messages["TaskWhenToPerformNursing"]) => {
      if (readonly) {
        return;
      }

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex(
        (answer) => answer.item.toLowerCase() === itemName.toLowerCase()
      );

      if (existingAnswerIndex >= 0) {
        const newAnswers = [...currentAnswers];
        newAnswers[existingAnswerIndex] = {
          ...newAnswers[existingAnswerIndex],
          attributes: whenToPerform,
        };
        onInsertAnswer(questionId, newAnswers);
      }
    },
    [currentAnswer, onInsertAnswer, questionId, readonly]
  );

  const handleFrequencyChange = useCallback(
    (itemName: string, frequency: number, visitsInAWeek: number) => {
      if (readonly) {
        return;
      }

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex(
        (answer) => answer.item.toLowerCase() === itemName.toLowerCase()
      );

      if (existingAnswerIndex >= 0) {
        const currentAnswer = currentAnswers[existingAnswerIndex];
        if (currentAnswer.attributes?.type === "OnDaysOfWeek") {
          const newAnswers = [...currentAnswers];
          newAnswers[existingAnswerIndex] = {
            ...currentAnswer,
            attributes: {
              ...currentAnswer.attributes,
              frequency: { frequency, visitsInAWeek },
            },
          };
          onInsertAnswer(questionId, newAnswers);
        }
      }
    },
    [currentAnswer, onInsertAnswer, questionId, readonly]
  );

  const handleNotesChange = useCallback(
    (itemName: string, notes: string) => {
      if (readonly) {
        return;
      }

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex(
        (answer) => answer.item.toLowerCase() === itemName.toLowerCase()
      );

      if (existingAnswerIndex >= 0) {
        const newAnswers = [...currentAnswers];
        newAnswers[existingAnswerIndex] = {
          ...newAnswers[existingAnswerIndex],
          notes,
        };
        onInsertAnswer(questionId, newAnswers);
      }
    },
    [currentAnswer, onInsertAnswer, questionId, readonly]
  );

  return {
    handleTaskCheck,
    handleWhenToPerformChange,
    handleFrequencyChange,
    handleNotesChange,
  };
}
